import React from 'react'
import { arrayOf, shape, string, number, bool } from 'prop-types'
import SwipeableViews from 'react-swipeable-views'
import { mod } from 'react-swipeable-views-core'
import { bindKeyboard, virtualize } from 'react-swipeable-views-utils'
import NumberFormat from 'react-number-format'
import { Link } from 'gatsby'
import styled from 'styled-components'
import { Flex, Box, Heading } from 'rebass'
import media, {
  LargeScreenUp,
  SmallToMediumScreenOnly,
  lazyload
} from '../../utils/style'

import LinkRounded from '../../components/LinkRounded'
import IconFormArrowRight from '../../vectors/IconFormArrowRight'
import {
  CardHeading,
  CardDetail,
  VehiclePrice,
  VehicleOutline,
  VehiclePreviewBox,
  VehiclePreviewWrapper,
  OfferRoundel,
  MotabilityRoundel,
  BannerVehicleOnOfferStripe
} from './components/VehiclePreview'
import {
  TransitionPrev,
  TransitionNext,
  TransitionButtonHolder
} from './components/TransitionCtrl'
import Image from '../Image'
import LazyLoad from 'react-lazyload'

const BindKeyboardSwipeableViews = bindKeyboard(SwipeableViews)
const BindVirtualizeKeyboardSwipeableViews = virtualize(
  bindKeyboard(SwipeableViews)
)

/*************************
  STYLES: WRAPPER
**************************/

const ContentFrame = styled(Box)`
  position: relative;
  overflow: hidden;
  z-index: 0;
  width: 100%;
  > div > div {
    -ms-overflow-x: visible !important;
    overflow-x: visible !important;
    width: 100%;
    ${media.MediumScreenUp`
      width: 55%;
    `}
    ${media.LargeScreenUp`
      width: auto;
    `}
  }
  > div {
    justify-content: center;
  }
  > div > div {
    transition: transform 0.4s ${(props) => props.theme.ease.ease_out_back};
  }
`

const FullWidthContentFrame = styled(ContentFrame)`
  max-width: 100vw;
  > div > div {
    width: 100%;
    ${media.MediumScreenUp`
      width: 55%;
    `}
    ${media.LargeScreenUp`
      width: 30%;
      transform: translateX(-50%);
    `}
  }
`
FullWidthContentFrame.displayName = 'FullWidthContentFrame'

class ContentVehicleMultiCardListing extends React.PureComponent {
  constructor(props) {
    super(props)
    const vehicles = props.vehicles.sort((a, b) => a.order - b.order)

    const start = vehicles.length > 2 ? 1 : 0
    this.state = {
      index: start,
      vehicles
    }
    this.loading = { prepend: false, append: false }
    this.next = this.next.bind(this)
    this.prev = this.prev.bind(this)
    this.handleChangeIndex = this.handleChangeIndex.bind(this)
    this.slideRenderer = this.slideRenderer.bind(this)
    this.getSlide = this.getSlide.bind(this)
  }

  handleChangeIndex(index) {
    this.setState({
      index
    })
  }

  next(e) {
    if (e) e.preventDefault()
    if (this.props.loop) {
      this.handleChangeIndex(this.state.index - 1)
    } else {
      if (this.state.index > 0) this.handleChangeIndex(this.state.index - 1)
    }
  }

  prev(e) {
    if (e) e.preventDefault()
    if (this.props.loop) {
      this.handleChangeIndex(this.state.index + 1)
    } else {
      if (this.state.index < this.state.vehicles.length - 1)
        this.handleChangeIndex(this.state.index + 1)
    }
  }

  slideRenderer(params) {
    const { index, key } = params
    const loopingIndex = mod(index, this.state.vehicles.length)
    return this.getSlide(this.state.vehicles[loopingIndex], index, key)
  }

  getSlide(vehicle, index, key = 'norepeat') {
    const isVehicleDetailsAvailable = vehicle.details.length > 0
    const vehicleEngineAndDetails = vehicle.details.slice(0)
    const isBelowMax = this.state.vehicles.length < this.props.max
    vehicle.engine && vehicleEngineAndDetails.unshift(vehicle.engine)
    const highlightMotability =
      this.props.highlightMotability && vehicle.motability
    const querystring =
      (typeof window !== 'undefined' && window.location.search) || ''

    const hasRoundel =
      (vehicle.offer && !vehicle.outlined) || highlightMotability
    const roundelText =
      !vehicle.roundel || vehicle.roundel === ''
        ? highlightMotability
          ? 'Motability Offer'
          : 'On Offer'
        : vehicle.roundel
    const RoundelComponent = highlightMotability
      ? MotabilityRoundel
      : OfferRoundel

    const vehicleDetails = vehicleEngineAndDetails.map((detail) => {
      return <span key={`vmcl:${detail}`}>{detail}</span>
    })

    return (
      <VehiclePreviewWrapper
        key={`vmcl:${index}:${vehicle.path}:${key}`}
        className={'transition-preview'}
        preview={index}
        focus={this.state.index}
        maxed={!isBelowMax}
        win={1}
      >
        <Link to={vehicle.path + querystring}>
          <VehiclePreviewBox
            px={[30, null, 40]}
            pt={[85]}
            pb={[40, null, 80]}
            reverse={this.props.reverse}
            outlined={vehicle.outlined}
            buttons={this.props.buttons}
          >
            <VehicleOutline outlined={vehicle.outlined}>
              {hasRoundel && (
                <RoundelComponent px={2} py={2}>
                  <span>{roundelText}</span>
                </RoundelComponent>
              )}
              {vehicle.offer && vehicle.outlined && (
                <BannerVehicleOnOfferStripe />
              )}
              <LazyLoad offset={lazyload.offset} height={190} once>
                <Image
                  resource={vehicle.image}
                  width="400"
                  height={this.props.fixedHeightImage ? 190 : null}
                  mode={this.props.fixedHeightImage ? 'pad' : null}
                  vehicleCategory={vehicle.category}
                  alt={vehicle.model}
                />
              </LazyLoad>
              <CardDetail
                preview={index}
                focus={this.state.index}
                outlined={vehicle.outlined}
                reverse={this.props.reverse}
              >
                <CardHeading
                  offer={vehicle.offer}
                  reverse={this.props.reverse}
                  as="h4"
                  fontSize={4}
                  mt={[2]}
                >
                  <span>
                    {this.props.withMake
                      ? `${vehicle.make} ${vehicle.model}`
                      : vehicle.model}
                  </span>
                  {isVehicleDetailsAvailable && (
                    <span>
                      <NumberFormat
                        value={vehicle.price.total}
                        displayType={'text'}
                        thousandSeparator
                        prefix={this.props.currency.symbol}
                      />
                    </span>
                  )}
                </CardHeading>
                {!isVehicleDetailsAvailable &&
                  !highlightMotability &&
                  vehicle.price &&
                  (typeof vehicle.price === 'string' && isNaN(vehicle.price) ? (
                    <VehiclePrice
                      reverse={this.props.reverse}
                      offer={vehicle.offer}
                      fontSize={2}
                      mt={12}
                      mb={15}
                    >
                      {vehicle.price}
                    </VehiclePrice>
                  ) : (
                    <VehiclePrice
                      reverse={this.props.reverse}
                      offer={vehicle.offer}
                      fontSize={2}
                      mt={12}
                      mb={15}
                    >
                      prices start from{' '}
                      <NumberFormat
                        value={vehicle.price.total}
                        displayType={'text'}
                        thousandSeparator
                        prefix={this.props.currency.symbol}
                      />
                    </VehiclePrice>
                  ))}
                {!isVehicleDetailsAvailable && highlightMotability && (
                  <div>
                    <VehiclePrice
                      reverse={this.props.reverse}
                      offer={vehicle.offer}
                      fontSize={2}
                      mt={12}
                      mb={15}
                    >
                      {vehicle.motability.downpayment}
                    </VehiclePrice>
                    <VehiclePrice
                      reverse={this.props.reverse}
                      offer={vehicle.offer}
                      fontSize={2}
                      mt={12}
                      mb={15}
                    >
                      {vehicle.motability.price}
                    </VehiclePrice>
                  </div>
                )}
                {isVehicleDetailsAvailable && (
                  <VehiclePrice
                    reverse={this.props.reverse}
                    offer={vehicle.offer}
                    fontSize={2}
                    my={'8px'}
                  >
                    {vehicleDetails}
                  </VehiclePrice>
                )}
                {this.props.buttons && (
                  <Box mt={[25, null, 30]}>
                    <LinkRounded
                      shadow
                      stretched
                      thin
                      as="div"
                      offer={vehicle.offer}
                    >
                      {vehicle.callToActionText || 'view details'}
                    </LinkRounded>
                  </Box>
                )}
              </CardDetail>
            </VehicleOutline>
          </VehiclePreviewBox>
        </Link>
      </VehiclePreviewWrapper>
    )
  }

  render() {
    const isBelowMax = this.state.vehicles.length < this.props.max
    const styles = {
      root: {
        margin: '0 auto',
        overflow: 'visible',
        MsOverflowX: 'visible'
      },
      slideContainer: {
        width: '100%',
        overflow: 'visible'
      }
    }

    this.vehiclePreviews =
      // eslint-disable-next-line prettier/prettier
      !this.props.loop || isBelowMax
        ? this.state.vehicles.map(this.getSlide)
        : null

    const swipeablePreviews = (
      <div>
        {this.props.heading !== '' && (
          <Heading fontSize={[5, 6, 7]} mt={60} mb={-30} center>
            {this.props.heading}
          </Heading>
        )}
        {this.props.loop && (
          <BindVirtualizeKeyboardSwipeableViews
            index={this.state.index}
            onChangeIndex={this.handleChangeIndex}
            style={styles.root}
            slideStyle={styles.slideContainer}
            slideRenderer={this.slideRenderer}
            overscanSlideAfter={4}
            overscanSlideBefore={4}
          />
        )}

        {!this.props.loop && (
          <BindKeyboardSwipeableViews
            index={this.state.index}
            onChangeIndex={this.handleChangeIndex}
            style={styles.root}
            slideStyle={styles.slideContainer}
          >
            {this.vehiclePreviews}
          </BindKeyboardSwipeableViews>
        )}
        <TransitionButtonHolder vehicles={this.state.vehicles.length}>
          {this.props.loop && (
            <TransitionPrev id="transition-prev" onClick={this.prev}>
              <IconFormArrowRight />
            </TransitionPrev>
          )}
          {this.props.loop && (
            <TransitionNext id="transition-next" onClick={this.next}>
              <IconFormArrowRight />
            </TransitionNext>
          )}

          {!this.props.loop && (
            <TransitionPrev
              id="transition-prev"
              disable={this.state.index === this.state.vehicles.length - 1}
              onClick={this.prev}
            >
              <IconFormArrowRight />
            </TransitionPrev>
          )}
          {!this.props.loop && (
            <TransitionNext
              id="transition-next"
              disable={this.state.index === 0}
              onClick={this.next}
            >
              <IconFormArrowRight />
            </TransitionNext>
          )}
        </TransitionButtonHolder>
      </div>
    )

    return (
      <div>
        {isBelowMax && (
          <ContentFrame width={12 / 12}>
            <LargeScreenUp>
              <Flex>{this.vehiclePreviews}</Flex>
            </LargeScreenUp>
            <SmallToMediumScreenOnly>
              {swipeablePreviews}
            </SmallToMediumScreenOnly>
          </ContentFrame>
        )}
        {!isBelowMax && (
          <FullWidthContentFrame id="no-swipe" width={12 / 12}>
            {swipeablePreviews}
          </FullWidthContentFrame>
        )}
      </div>
    )
  }
}

ContentVehicleMultiCardListing.defaultProps = {
  vehicles: [],
  details: [],
  currency: { code: 'GBP', symbol: '£' },
  max: 4,
  loop: false,
  reverse: false,
  heading: '',
  fixedHeightImage: true,
  withMake: false
}

ContentVehicleMultiCardListing.propTypes = {
  vehicles: arrayOf(
    shape({
      model: string,
      derivative: string,
      engine: string,
      details: arrayOf(string),
      price: shape({
        total: number,
        installment: shape({ payment: number, plan: string, code: string })
      }),
      path: string,
      image: string,
      offer: bool,
      roundel: string,
      outlined: bool,
      callToActionText: string
    })
  ),
  currency: shape({ code: string, symbol: string }),
  buttons: bool,
  loop: bool,
  max: number,
  reverse: bool,
  heading: string,
  withMake: bool
}

export default ContentVehicleMultiCardListing
